export interface PersonFolderBreadcrumb {
  id: string;
  name: string;
}

export interface PersonFolderNode {
  id: string;
  name: string;
  parentId?: string | null;
}

export type PersonDriveFolderMatchQuality = "exact" | "normalized" | "forgiving" | "none";

export interface PersonDriveFolderNameMatchResult {
  matchQuality: PersonDriveFolderMatchQuality;
  matchedCandidateName?: string;
}

const PERSON_FOLDER_STATUS_MARKERS = ["🟢", "🟥", "🟠", "🟣", "🚫", "🔲", "🫥", "🟪"] as const;
const FIRST_NAME_ALIAS_GROUPS = [
  ["alex", "alexander", "alexandra"],
  ["andy", "andrew"],
  ["ben", "benjamin", "benny"],
  ["chris", "christopher"],
  ["dan", "daniel", "danny"],
  ["dave", "david"],
  ["dom", "dominic"],
  ["jack", "jackson"],
  ["jamie", "james"],
  ["joe", "joseph"],
  ["jon", "john", "jonathan"],
  ["kate", "katherine", "kathryn", "katie"],
  ["matt", "matthew"],
  ["mike", "michael"],
  ["nick", "nicholas", "nicolas"],
  ["rob", "robert", "robbie"],
  ["sam", "samuel", "samantha"],
  ["steve", "steven", "stephen"],
  ["tom", "thomas", "tommy"],
] as const;

const FIRST_NAME_ALIAS_LOOKUP = new Map<string, string>(
  FIRST_NAME_ALIAS_GROUPS.flatMap((group) => group.map((entry) => [entry, group[0]] as const)),
);

function getPersonFolderStatusMarker(person: any, profile: any) {
  const employmentStatus = String(profile?.employmentStatus || person?.employmentStatus || "")
    .trim()
    .toLowerCase();
  const accessStatus = String(profile?.status || person?.status || "")
    .trim()
    .toLowerCase();

  if (person?.active === false || employmentStatus === "former" || employmentStatus === "inactive") {
    return "🟥";
  }

  if (["offboarding", "off_boarding", "suspended", "revoked"].includes(accessStatus)) {
    return "🟣";
  }

  if (["onboarding", "invited", "pending_approval", "pending approval"].includes(accessStatus)) {
    return "🟠";
  }

  if (["inactive", "disabled"].includes(accessStatus)) {
    return "🟥";
  }

  return "🟢";
}

function derivePersonNameParts(person: any, profile: any) {
  const explicitFirstName = String(person?.firstName || profile?.firstName || "").trim();
  const explicitLastName = String(person?.lastName || profile?.lastName || "").trim();
  if (explicitFirstName || explicitLastName) {
    return {
      firstName: explicitFirstName,
      lastName: explicitLastName,
    };
  }

  const fullName =
    String(person?.name || "").trim() ||
    String(person?.displayName || "").trim() ||
    String(profile?.fullName || "").trim() ||
    "";

  if (!fullName) {
    return { firstName: "", lastName: "" };
  }

  if (fullName.includes(",")) {
    const [lastPart, firstPart] = fullName.split(",", 2);
    return {
      firstName: String(firstPart || "").trim(),
      lastName: String(lastPart || "").trim(),
    };
  }

  const tokens = fullName.split(/\s+/).filter(Boolean);
  if (tokens.length === 1) {
    return { firstName: tokens[0], lastName: "" };
  }

  return {
    firstName: tokens.slice(0, -1).join(" ").trim(),
    lastName: tokens[tokens.length - 1] || "",
  };
}

export function buildPersonDriveFolderNames(person: any, profile: any) {
  const { firstName, lastName } = derivePersonNameParts(person, profile);
  const statusMarker = getPersonFolderStatusMarker(person, profile);

  const basePreferredName =
    firstName && lastName
      ? `${lastName}, ${firstName}`
      : [firstName, lastName].filter(Boolean).join(" ").trim() ||
        String(person?.name || "").trim() ||
        String(person?.displayName || "").trim() ||
        String(profile?.driveFolderName || "").trim() ||
        "Person";

  const alternateLegacyName =
    firstName && lastName
      ? `${firstName} ${lastName}`
      : [firstName, lastName].filter(Boolean).join(" ").trim() || basePreferredName;

  return {
    preferredName: `${statusMarker} ${basePreferredName}`.trim(),
    markerlessPreferredName: basePreferredName,
    legacyName: alternateLegacyName,
  };
}

export function stripPersonDriveFolderStatusMarker(name: string) {
  const trimmed = String(name || "").trim();
  if (!trimmed) return "";
  const markerPattern = PERSON_FOLDER_STATUS_MARKERS.map((marker) => marker.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");
  return trimmed.replace(new RegExp(`^(?:${markerPattern})\\s*`, "u"), "").trim();
}

export function normalizePersonDriveFolderNameForMatching(name: string) {
  return stripPersonDriveFolderStatusMarker(name)
    .normalize("NFKC")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function normalizeLooseNameToken(value: string) {
  return String(value || "")
    .normalize("NFKC")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function canonicalizeFirstNameToken(value: string) {
  const normalized = normalizeLooseNameToken(value).split(" ")[0] || "";
  return FIRST_NAME_ALIAS_LOOKUP.get(normalized) || normalized;
}

function deriveLooseNameParts(value: string) {
  const raw = stripPersonDriveFolderStatusMarker(value);
  if (!raw) return null;

  if (raw.includes(",")) {
    const [lastPart, firstPart] = raw.split(",", 2);
    const lastName = normalizeLooseNameToken(lastPart);
    const firstName = normalizeLooseNameToken(firstPart).split(" ")[0] || "";
    if (!firstName && !lastName) return null;
    return { firstName, lastName };
  }

  const tokens = normalizeLooseNameToken(raw).split(" ").filter(Boolean);
  if (tokens.length === 0) return null;
  if (tokens.length === 1) {
    return { firstName: tokens[0], lastName: "" };
  }
  return {
    firstName: tokens[0],
    lastName: tokens[tokens.length - 1],
  };
}

function firstNamesLooselyMatch(left: string, right: string) {
  if (!left || !right) return false;
  if (left === right) return true;

  const canonicalLeft = canonicalizeFirstNameToken(left);
  const canonicalRight = canonicalizeFirstNameToken(right);
  if (canonicalLeft && canonicalLeft === canonicalRight) return true;

  const minimumPrefixLength = 3;
  return (
    (left.length >= minimumPrefixLength && right.startsWith(left)) ||
    (right.length >= minimumPrefixLength && left.startsWith(right))
  );
}

export function classifyPersonDriveFolderNameMatch(
  folderName: string,
  candidateNames: string[],
): PersonDriveFolderNameMatchResult {
  const rawFolderName = String(folderName || "").trim();
  const normalizedFolderName = normalizePersonDriveFolderNameForMatching(rawFolderName);
  const normalizedCandidates = candidateNames
    .map((candidate) => String(candidate || "").trim())
    .filter(Boolean);

  const exactCandidate = normalizedCandidates.find((candidate) => candidate === rawFolderName);
  if (exactCandidate) {
    return { matchQuality: "exact", matchedCandidateName: exactCandidate };
  }

  const normalizedCandidate = normalizedCandidates.find(
    (candidate) => normalizePersonDriveFolderNameForMatching(candidate) === normalizedFolderName,
  );
  if (normalizedCandidate) {
    return { matchQuality: "normalized", matchedCandidateName: normalizedCandidate };
  }

  const folderParts = deriveLooseNameParts(rawFolderName);
  if (!folderParts?.firstName || !folderParts?.lastName) {
    return { matchQuality: "none" };
  }

  for (const candidate of normalizedCandidates) {
    const candidateParts = deriveLooseNameParts(candidate);
    if (!candidateParts?.firstName || !candidateParts?.lastName) continue;
    if (candidateParts.lastName !== folderParts.lastName) continue;
    if (!firstNamesLooselyMatch(candidateParts.firstName, folderParts.firstName)) continue;
    return { matchQuality: "forgiving", matchedCandidateName: candidate };
  }

  return { matchQuality: "none" };
}

export function extractGoogleDriveFolderId(input: string) {
  const trimmed = String(input || "").trim();
  if (!trimmed) return "";

  const folderMatch = trimmed.match(/\/folders\/([a-zA-Z0-9_-]+)/);
  if (folderMatch?.[1]) return folderMatch[1];

  const queryMatch = trimmed.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (queryMatch?.[1]) return queryMatch[1];

  const directIdMatch = trimmed.match(/^[a-zA-Z0-9_-]{10,}$/);
  if (directIdMatch?.[0]) return directIdMatch[0];

  return "";
}

export function resolvePersonScopedBreadcrumbs(
  folders: PersonFolderNode[],
  rootFolderId: string,
  requestedFolderId?: string | null,
) {
  const targetFolderId = requestedFolderId || rootFolderId;
  const folderMap = new Map(folders.map((folder) => [folder.id, folder]));
  const breadcrumbs: PersonFolderBreadcrumb[] = [];

  let currentId: string | null = targetFolderId;
  let safety = 0;

  while (currentId && safety < 50) {
    safety += 1;
    const folder = folderMap.get(currentId);
    if (!folder) {
      if (breadcrumbs.length === 0) {
        throw new Error("FOLDER_NOT_FOUND");
      }
      throw new Error("FOLDER_OUTSIDE_PERSON_ROOT");
    }
    breadcrumbs.unshift({
      id: folder.id,
      name: folder.name || "Folder",
    });
    if (folder.id === rootFolderId) {
      return {
        currentFolderId: targetFolderId,
        currentFolderName: folderMap.get(targetFolderId)?.name || "Folder",
        breadcrumbs,
      };
    }
    currentId = folder.parentId || null;
  }

  throw new Error("FOLDER_OUTSIDE_PERSON_ROOT");
}
